import { useState, useEffect, useMemo, useRef } from 'react';
import { getUserPlanLimits } from './usePlan';

interface QuotaData {
  currentUsage: number;
  monthlyLimit: number;
  resetDate: Date;
  remainingDays: number;
  percentageUsed: number;
  isOverLimit: boolean;
  isLoading: boolean;
  error: string | null;
  planName?: string;
}

interface UseMonthlyQuotaOptions {
  userEmail?: string;
  shopDomain?: string;
  monthlyLimit?: number;
}

// Cache for quota data (expires every 5 minutes)
const quotaCache = new Map<string, { data: any; timestamp: number }>();
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

// Shared cache key for user plan data to avoid duplicate calls
const userPlanCache = new Map<string, { data: any; timestamp: number }>();
const USER_PLAN_CACHE_DURATION = 2 * 60 * 1000; // 2 minutes

export function useMonthlyQuota({
  userEmail,
  shopDomain,
  monthlyLimit: fallbackLimit = 100
}: UseMonthlyQuotaOptions = {}): QuotaData {
  const [currentUsage, setCurrentUsage] = useState(0);
  const [actualMonthlyLimit, setActualMonthlyLimit] = useState(fallbackLimit);
  const [planName, setPlanName] = useState<string>('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const hasFetchedRef = useRef(false);

  
  // State for reset date and remaining days
  const [resetDate, setResetDate] = useState<Date>(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth() + 1, 1);
  });
  const [remainingDays, setRemainingDays] = useState(0);

  // Calculate reset date and remaining days on client side only
  useEffect(() => {
    const now = new Date();
    const nextMonth = new Date(now.getFullYear(), now.getMonth() + 1, 1);
    setResetDate(nextMonth);
    
    const diffTime = nextMonth.getTime() - now.getTime();
    setRemainingDays(Math.ceil(diffTime / (1000 * 60 * 60 * 24)));
  }, []);

  // Calculate percentage used
  const percentageUsed = useMemo(() => {
    if (actualMonthlyLimit === Infinity) return 0;
    return Math.min((currentUsage / actualMonthlyLimit) * 100, 100);
  }, [currentUsage, actualMonthlyLimit]);

  // Check if over limit
  const isOverLimit = useMemo(() => {
    if (actualMonthlyLimit === Infinity) return false;
    return currentUsage >= actualMonthlyLimit;
  }, [currentUsage, actualMonthlyLimit]);

  // Fetch current month's game usage
  useEffect(() => {
    if (!userEmail && !shopDomain) {
      setIsLoading(false);
      return;
    }

    // Prevent duplicate fetches
    if (hasFetchedRef.current) {
      return;
    }

    const fetchData = async () => {
      try {
        setIsLoading(true);
        setError(null);

        // Check cache first
        const cacheKey = `${userEmail}_${shopDomain}`;
        const cachedData = quotaCache.get(cacheKey);
        
        // Use a stable timestamp to avoid hydration mismatch
        const now = typeof window !== 'undefined' ? Date.now() : 0;

        if (cachedData && (now - cachedData.timestamp) < CACHE_DURATION) {
          setCurrentUsage(cachedData.data.currentUsage);
          setActualMonthlyLimit(cachedData.data.monthlyLimit);
          setPlanName(cachedData.data.planName);
          setIsLoading(false);
          return;
        }

        let monthlyLimit = fallbackLimit;
        let planNameValue = '';

        // First, fetch user's plan limits (this function now has its own caching)
        const planLimits = await getUserPlanLimits(userEmail, shopDomain);
        if (planLimits) {
          monthlyLimit = planLimits.monthlyPlays;
          
          // Get plan name from cached data to avoid duplicate API call
          const planCacheKey = `${userEmail}_${shopDomain}`;
          const cachedPlanData = userPlanCache.get(planCacheKey);
          if (cachedPlanData && cachedPlanData.data?.planId) {
            planNameValue = cachedPlanData.data.planId.charAt(0).toUpperCase() + cachedPlanData.data.planId.slice(1);
          } else {
            // Fallback to API call if cache miss
            const planResponse = await fetch(`/api/user-plan?userEmail=${encodeURIComponent(userEmail || '')}&shopDomain=${encodeURIComponent(shopDomain || '')}`);
            if (planResponse.ok) {
              const planData = await planResponse.json();
              if (planData.success && planData.data?.planId) {
                planNameValue = planData.data.planId.charAt(0).toUpperCase() + planData.data.planId.slice(1);
              }
            }
          }
        }

        // Set the values after fetching
        setActualMonthlyLimit(monthlyLimit);
        setPlanName(planNameValue);

        // Get current month's start and end dates
        const nowDate = new Date();
        const monthStart = new Date(nowDate.getFullYear(), nowDate.getMonth(), 1);
        const monthEnd = new Date(nowDate.getFullYear(), nowDate.getMonth() + 1, 0, 23, 59, 59);

        // Fetch games for current month
        // Note: plays-stats is a loader function (GET), not an action (POST)
        const url = new URL('/api/plays-stats', window.location.origin);
        if (userEmail) url.searchParams.set('userEmail', userEmail);
        if (shopDomain) url.searchParams.set('shopDomain', shopDomain);
        url.searchParams.set('startDate', monthStart.toISOString());
        url.searchParams.set('endDate', monthEnd.toISOString());

        const response = await fetch(url.toString(), {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          }
        });

        if (!response.ok) {
          throw new Error(`Failed to fetch monthly usage: ${response.status} ${response.statusText}`);
        }

        const data = await response.json();
        
        if (data.success) {
          const totalPlays = data.data?.totalPlays || 0;
          setCurrentUsage(totalPlays);

          // Update cache with the actual values we fetched
          quotaCache.set(cacheKey, {
            data: {
              currentUsage: totalPlays,
              monthlyLimit: monthlyLimit,
              planName: planNameValue
            },
            timestamp: now
          });
        } else {
          throw new Error(data.error || 'Failed to fetch usage data');
        }
      } catch (err) {
        console.error('Error fetching monthly quota usage:', err);
        setError(err instanceof Error ? err.message : 'Unknown error');
        
        // Fallback to 0 usage if API fails
        setCurrentUsage(0);
      } finally {
        setIsLoading(false);
        hasFetchedRef.current = true;
      }
    };

    fetchData();
  }, [userEmail, shopDomain, fallbackLimit]);

  // Return quota data
  return {
    currentUsage,
    monthlyLimit: actualMonthlyLimit,
    resetDate,
    remainingDays,
    percentageUsed,
    isOverLimit,
    isLoading,
    error,
    planName
  };
}

// Helper function to get month name
export function getMonthName(date: Date): string {
  return date.toLocaleDateString('en-US', { month: 'long' });
}

// Helper function to format remaining time
export function formatRemainingTime(remainingDays: number): string {
  if (remainingDays <= 0) return 'Resets today';
  if (remainingDays === 1) return 'Resets tomorrow';
  if (remainingDays < 7) return `Resets in ${remainingDays} days`;
  
  const weeks = Math.floor(remainingDays / 7);
  const days = remainingDays % 7;
  
  if (weeks === 1 && days === 0) return 'Resets in 1 week';
  if (weeks > 1 && days === 0) return `Resets in ${weeks} weeks`;
  if (weeks > 1) return `Resets in ${weeks} weeks, ${days} days`;
  
  return `Resets in ${remainingDays} days`;
}
